/**
 * MFA Service
 *
 * Handles Multi-Factor Authentication operations
 */

import { BaseService } from "../base/BaseService";
import {
  MFAEnrollResponse,
  MFAStatusResponse,
  VerifyMFAEnrollmentResponse,
  CreateMFAChallengeResponse,
  VerifyMFALoginResponse,
  UnenrollMFAResponse,
  VerifyRecoveryCodeResponse,
} from "./types";

const AUTH_SERVICE_URL = "auth";

/**
 * MFA Service
 * Handles all MFA-related operations
 */
export class MFAService extends BaseService {
  constructor() {
    super(AUTH_SERVICE_URL);
  }

  /**
   * Enroll in MFA
   * POST /mfa/enroll
   */
  enrollMFA(userId: string): Promise<MFAEnrollResponse> {
    return this.post("/mfa/enroll", { userId });
  }

  /**
   * Verify MFA enrollment
   * POST /mfa/verify
   */
  verifyMFAEnrollment(
    userId: string,
    factorId: string,
    code: string
  ): Promise<VerifyMFAEnrollmentResponse> {
    return this.post("/mfa/verify", { userId, factorId, code });
  }

  /**
   * Create MFA challenge for login
   * POST /mfa/challenge
   */
  createMFAChallenge(userId: string, factorId: string): Promise<CreateMFAChallengeResponse> {
    return this.post("/mfa/challenge", { userId, factorId });
  }

  /**
   * Verify MFA code during login
   * POST /mfa/verify-login
   */
  verifyMFALogin(
    userId: string,
    factorId: string,
    challengeId: string,
    code: string
  ): Promise<VerifyMFALoginResponse> {
    return this.post("/mfa/verify-login", { userId, factorId, challengeId, code });
  }

  /**
   * Unenroll from MFA
   * POST /mfa/unenroll
   */
  unenrollMFA(userId: string, factorId: string): Promise<UnenrollMFAResponse> {
    return this.post("/mfa/unenroll", { userId, factorId });
  }

  /**
   * Get MFA status
   * GET /mfa/status
   */
  getMFAStatus(userId: string): Promise<MFAStatusResponse> {
    return this.get(`/mfa/status?userId=${userId}`);
  }

  /**
   * Generate recovery codes
   * POST /mfa/recovery-codes
   */
  generateRecoveryCodes(userId: string): Promise<{ codes: string[] }> {
    return this.post("/mfa/recovery-codes", { userId });
  }

  /**
   * Verify recovery code during login
   * POST /mfa/verify-recovery-code
   */
  verifyRecoveryCode(userId: string, code: string): Promise<VerifyRecoveryCodeResponse> {
    return this.post("/mfa/verify-recovery-code", { userId, code });
  }

  syncMFAEnrollment(userId: string, factorId: string): Promise<{ success: boolean }> {
    return this.post("/mfa/sync-enrollment", { userId, factorId });
  }
}
